import React, { ChangeEvent, FC, useEffect, useCallback } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { useSelector } from 'react-redux'
import { yupResolver } from '@hookform/resolvers'
import { AddressAutosuggest, IAddressAutosuggestValue } from 'components/atoms/AddressAutosuggest'
import { ILocationSuggestion } from 'transfix-ui/components/SuggestLocation'
import { useFacilityFormAutoComplete } from 'utils/hooks/useFacilityFormAutoComplete'
import { v4 as uuid } from 'uuid'
import {
  LocationAutosuggest,
  getDefaultValue,
} from 'apps/fleet-planner/components/atoms/LocationAutosuggest'
import {
  AddEntityForm,
  AddEntityFormFooter,
} from 'apps/fleet-planner/components/forms/AddEntityForm'
import { IAddNewEntityFormProps } from 'apps/fleet-planner/components/forms/TrailerForm'
import { INewAddress } from 'apps/fleet-planner/schema/AddressSchema'
import { FacilitySchema, FacilitySchemaType } from 'apps/fleet-planner/schema/FacilitySchema'
import { IFacilityAddress } from 'apps/fleet-planner/utils/api'
import { recentFacilitiesStateSelector } from 'apps/fleet-planner/utils/selectors'
import { TextInput } from 'components/atoms'
import { Button, PhoneInput } from 'transfix-ui'
import { formatPhone, unformatPhone } from 'utils/formatPhone'

/**
 * Formats the address for <AddressAutocomplete />
 */
const getAddressValue = (address: Partial<INewAddress>): Partial<IAddressAutosuggestValue> => {
  const { addressLine1 = '', location, postalCode = '' } = address || {}
  const { locality: city = '', subdivisionCode: state = '' } = location || {}
  return {
    city,
    state,
    addressLine1,
    postalCode: postalCode ?? '',
  }
}

const emptyFacilityState: FacilitySchemaType = {
  address: {
    addressee: '',
    addressLine1: '',
    addressLine2: '',
    postalCode: '',
    location: {
      locality: '',
      subdivisionCode: '',
    },
  },
  contact: {
    name: '',
    email: '',
    phone: '',
    phoneExt: '',
  },
  name: '',
}

interface IFacilityLocation extends Pick<IFacilityAddress, 'locality' | 'subdivisionCode' | 'id'> {}

/**
 * Helper method which only returns an `id` if city and state are defined
 *
 * @note `id` value is needed for `<LocationAutosuggest />` to know which
 * value is currently selected.
 *
 * For locations passed with no id, this generates a unique id to reference.
 */
const getDefaultLocationId = (location?: IFacilityLocation): string => {
  const { id = uuid(), subdivisionCode, locality } = location || {}
  if (!(subdivisionCode || locality)) return ''
  return id
}

/**
 * Removes all null values
 */
const getDefaultFacilityValues = (data: Partial<FacilitySchemaType>): FacilitySchemaType => {
  const { address, contact, name } = data || {}
  return {
    address: {
      addressee: address?.addressee ?? '',
      addressLine1: address?.addressLine1 ?? '',
      addressLine2: address?.addressLine2 ?? '',
      postalCode: address?.postalCode ?? '',
      location: {
        id: getDefaultLocationId(address?.location),
        locality: address?.location?.locality ?? '',
        subdivisionCode: address?.location?.subdivisionCode ?? '',
      },
    },
    contact: {
      name: contact?.name ?? '',
      email: contact?.email ?? '',
      phone: contact?.phone ?? '',
      phoneExt: contact?.phoneExt ?? '',
    },
    name: name ?? '',
  }
}

export interface IFacilityFormProps extends IAddNewEntityFormProps<FacilitySchemaType> {
  setHasErrors?: (T: boolean) => void
}

export const FacilityForm: FC<IFacilityFormProps> = ({
  onSuccess: onSuccessProp,
  onCancel: onCancelProp = () => {},
  defaultValues: defaultValuesProp = {},
  setHasErrors = () => {},
}) => {
  const { facilities = [] } = useSelector(recentFacilitiesStateSelector)
  const defaultValues = getDefaultFacilityValues(defaultValuesProp)
  const { control, errors, formState, handleSubmit, register, setValue } = useForm({
    mode: 'all',
    resolver: yupResolver(FacilitySchema),
    context: {
      facilities,
      isNewFacility: !!defaultValues.address?.addressLine1,
      originalFacilityName: defaultValues.name,
      isContactInfoRequired: false,
    },
    defaultValues: FacilitySchema.cast({
      ...emptyFacilityState,
      ...defaultValues,
    }),
  })

  const {
    touched: {
      name: isFacilityNameTouched = false,
      address: { location: isLocalityTouched, postalCode: isPostalCodeTouched = false } = {},
    },
    dirtyFields: {
      name: isFacilityNameDirty = false,
      address: { location: isLocalityDirty, postalCode: isPostalCodeDirty = false } = {},
    },
  } = formState

  const { facility, setAddress, setName } = useFacilityFormAutoComplete({
    defaultValues: {
      ...defaultValues,
      address: {
        ...defaultValues.address,
        postalCode: defaultValues.address.postalCode ?? '',
      },
    },
    setValue,
  })

  /**
   * This is used to trigger a re-render in RHF. For some reason
   * <SuggestLocation /> only registers it's value after a re-render
   */
  useEffect(() => {
    setValue('address.location.locality', defaultValues.address.location.locality)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const submitHandler = (data: FacilitySchemaType) => {
    onSuccessProp(data)
  }

  const isEditingExistingFacility = !!defaultValuesProp?.address?.addressLine1

  const buttonText = isEditingExistingFacility ? 'Save Changes' : 'Add Facility'

  useEffect(() => {
    const hasErrors = Object.keys({ ...errors }).length !== 0
    if (hasErrors) setHasErrors(hasErrors)
  }, [errors])

  /**
   * Because this changes several form models, it must exist outisde of the RHF context
   * and use useEffect pattern to update form state
   */
  const addressChangeHandler = useCallback(
    (change: Partial<IAddressAutosuggestValue> | undefined) => {
      const newAddress = {
        // Will always have this value
        // fall back to the label as some POIs have no address line 1
        addressLine1: change?.addressLine1 || change?.label || '',

        // May also have location data, if selected a mapbox option
        location: change?.id
          ? {
              id: change.id,
              locality: change.city || '',
              subdivisionCode: change.state || '',
            }
          : undefined,
        postalCode: change?.postalCode ?? '',
      }

      // Blend previous values and new
      setAddress(prev => ({
        ...prev,
        ...newAddress,
      }))

      setName(change?.name || '')
    },
    []
  )

  // Formats the value to `IAddressAutosuggestValue` type
  const addressValue = facility?.address && getAddressValue(facility.address)

  return (
    <AddEntityForm onSubmit={handleSubmit(submitHandler)} className='add-entity-form'>
      {/* Only show if this is a new address */}
      {!isEditingExistingFacility && <div className='mt4 mb2 body-medium'>Name and Address</div>}

      {/* To update this field programmatically, we need to initialize it in RHF via hidden input */}
      <input type='hidden' name='address.addressLine1' ref={register()} />

      {isEditingExistingFacility && (
        <>
          {' '}
          <div className='mb4 body-medium'>Address</div>
          <div className='mb4'>
            Changes to this facility’s address won’t affect loads you’ve already created.
          </div>
        </>
      )}
      {/* Address Line 1 */}
      <div className='mb4'>
        <AddressAutosuggest
          large
          required
          value={addressValue}
          onChange={addressChangeHandler}
          icon='locationPin'
          label='Address Line 1'
          dataTest='facility-address-addressLine1-input'
          error={errors?.address?.addressLine1?.message}
        />
      </div>

      {/* Address Line 2 */}
      <div className='mb4'>
        <Controller
          name='address.addressLine2'
          control={control}
          render={({ onBlur, onChange, value }) => {
            const changeHandler = (e: ChangeEvent<HTMLInputElement>) => {
              onChange(e.target.value)
            }

            return (
              <TextInput
                label='Facility Address 2'
                value={value}
                placeholder='Unit, building, lot, dock, suite, etc.'
                onChange={changeHandler}
                large
                onBlur={onBlur}
                validationMessage={errors?.address?.addressLine2?.message}
                invalid={!!errors?.address?.addressLine2?.message}
                dataTest='facility-address-addressLine2-input'
              />
            )
          }}
        />
      </div>

      <div className='flex'>
        <div className='flex-equal pr2'>
          {/* City */}
          <div className='mb4'>
            <Controller
              name='address.location'
              control={control}
              render={({ onBlur, onChange, value }) => {
                const { id: locationId, locality, subdivisionCode } = value || {}

                const changeHandler = (change: ILocationSuggestion | undefined) => {
                  const { city, state } = change || {}
                  onChange({
                    locality: city,
                    subdivisionCode: state,
                    id: change?.id,
                  })
                }

                const val = getDefaultValue({
                  city: locality,
                  state: subdivisionCode,
                  id: locationId,
                })

                const errorMessage =
                  errors?.address?.location?.locality?.message ||
                  errors?.address?.location?.subdivisionCode?.message

                return (
                  <LocationAutosuggest
                    large
                    required
                    icon='locationPin'
                    label='City'
                    placeholder='e.g. Houston, TX'
                    dataTest='facility-address-locality-input'
                    onChange={changeHandler}
                    onBlur={onBlur}
                    error={errorMessage}
                    value={val}
                    highlighted={!isLocalityTouched && !!isLocalityDirty}
                  />
                )
              }}
            />
          </div>
        </div>
        <div className='flex-equal pl2'>
          {/* Zip code */}
          <div className='mb4'>
            <Controller
              name='address.postalCode'
              control={control}
              render={({ onBlur, onChange, value }) => {
                const changeHandler = (e: ChangeEvent<HTMLInputElement>) => {
                  onChange(e.target.value)
                }

                return (
                  <TextInput
                    label='Zip Code'
                    value={value}
                    onChange={changeHandler}
                    large
                    onBlur={onBlur}
                    validationMessage={errors?.address?.postalCode?.message}
                    invalid={!!errors?.address?.postalCode?.message}
                    dataTest='facility-address-postalCode-input'
                    highlighted={!isPostalCodeTouched && isPostalCodeDirty ? 'info' : undefined}
                  />
                )
              }}
            />
          </div>
        </div>
      </div>

      {/* Facility name */}
      <div className='mb4'>
        <Controller
          name='name'
          control={control}
          render={({ onBlur, onChange, value }) => {
            const changeHandler = (e: ChangeEvent<HTMLInputElement>) => {
              onChange(e.target.value)
            }

            return (
              <TextInput
                large
                required
                label='Facility Name'
                value={value}
                placeholder='e.g. Houston Warehouse'
                onChange={changeHandler}
                onBlur={onBlur}
                validationMessage={errors?.name?.message}
                invalid={!!errors?.name?.message}
                dataTest='facility-name-input'
                highlighted={!isFacilityNameTouched && isFacilityNameDirty ? 'info' : undefined}
              />
            )
          }}
        />
      </div>

      {!isEditingExistingFacility && (
        <div className='mt4 body-medium mb2'>Contact Information (optional)</div>
      )}

      {isEditingExistingFacility && (
        <>
          <div className='mb4 body-medium'>Facility Contact</div>
          <div className='mb4'>
            Changes to your facility contact won’t affect any loads you’ve already created.
          </div>
        </>
      )}

      {/* Customer contact name */}
      <div className='mb4'>
        <Controller
          name='contact.name'
          control={control}
          render={({ onBlur, onChange, value }) => {
            const changeHandler = (e: ChangeEvent<HTMLInputElement>) => {
              onChange(e.target.value)
            }

            return (
              <TextInput
                label='Contact Name'
                value={value}
                onChange={changeHandler}
                placeholder='e.g. Jane Doe or Customer Service Department'
                large
                onBlur={onBlur}
                validationMessage={errors?.contact?.name?.message}
                invalid={!!errors?.contact?.name?.message}
                dataTest='facility-contact-name-input'
              />
            )
          }}
        />
      </div>

      <div className='flex'>
        <div className='flex-equal pr2'>
          {/* Customer contact phone */}
          <div className='mb4'>
            <Controller
              name='contact.phone'
              control={control}
              render={({ onBlur, onChange, value }) => {
                const formattedValue = formatPhone(value, { removeCountryCode: '+1' })
                const changeHandler = (e: ChangeEvent<HTMLInputElement>) => {
                  onChange(unformatPhone(e.target.value))
                }

                return (
                  <PhoneInput
                    className='fs-mask'
                    autoComplete='off'
                    label='Phone'
                    onChange={changeHandler}
                    onBlur={onBlur}
                    placeholder='(000) 000-0000'
                    value={formattedValue}
                    large
                    validationMessage={errors?.contact?.phone?.message}
                    invalid={!!errors?.contact?.phone?.message}
                    dataTest='facility-contact-phone-input'
                  />
                )
              }}
            />
          </div>
        </div>
        <div className='flex-equal pl2'>
          {/* Customer contact phone extension */}
          <div className='mb4'>
            <Controller
              name='contact.phoneExt'
              control={control}
              render={({ onBlur, onChange, value }) => {
                const changeHandler = (e: ChangeEvent<HTMLInputElement>) => {
                  onChange(e.target.value)
                }

                return (
                  <TextInput
                    label='Extension'
                    value={value}
                    onChange={changeHandler}
                    large
                    onBlur={onBlur}
                    validationMessage={errors?.contact?.phoneExt?.message}
                    invalid={!!errors?.contact?.phoneExt?.message}
                    dataTest='facility-contact-phoneExt-input'
                  />
                )
              }}
            />
          </div>
        </div>
      </div>

      {/* Facility contact email */}
      <div className='mb5'>
        <Controller
          name='contact.email'
          control={control}
          render={({ onBlur, onChange, value }) => {
            const changeHandler = (e: ChangeEvent<HTMLInputElement>) => {
              onChange(e.target.value)
            }

            return (
              <TextInput
                label='Email'
                value={value}
                onChange={changeHandler}
                placeholder='jane.doe@domain.com'
                large
                onBlur={onBlur}
                validationMessage={errors?.contact?.email?.message}
                invalid={!!errors?.contact?.email?.message}
                dataTest='facility-contact-email-input'
                className='fs-mask'
              />
            )
          }}
        />
      </div>

      <AddEntityFormFooter>
        <Button
          color='tertiary'
          size='large'
          onClick={onCancelProp}
          data-test='cancel-new-facility'>
          Cancel
        </Button>
        <Button color='primary' size='large' type='submit' data-test='save-new-facility'>
          {buttonText}
        </Button>
      </AddEntityFormFooter>
    </AddEntityForm>
  )
}

export default FacilityForm
